import React, { useEffect, useState } from "react";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "../../firebaseConfig";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import imageCompression from "browser-image-compression"; // Per comprimere le immagini
import useAllPropertiesData from "../../utils/hooks/usePropertyData";
import usePropertyPlan from "../../utils/hooks/usePropertyPlan";
import { Stack } from "@mui/system";

const FileUpload = ({
  folder,
  title,
  field,
  label,
  accept,
  multiple = true,
  isEdit,
  onChange,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [urls, setUrls] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [visibleImages, setVisibleImages] = useState([]); // Aggiungi stato per tenere traccia delle immagini visibili

  const { propertiesData, loading } = useAllPropertiesData(title); // Custom hook per ottenere i dati delle proprietà
  const { planimetriaData } = usePropertyPlan(title, folder); // Custom hook per ottenere la planimetria
  const { planimetriaData: videoData } = usePropertyPlan(title, "video"); // Custom hook per ottenere i dati del video

  const document = propertiesData?.[0]; // Usa optional chaining per evitare errori se propertiesData è undefined
  const planimetria = planimetriaData?.[0]; // Lo stesso controllo per planimetriaData
  const video = videoData?.[0]; // E per videoData

  const images = planimetria?.photos || []; // Se 'photos' non esiste, restituisci un array vuoto

  // Funzione per comprimere le immagini
  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error("Compression failed", error);
      throw new Error("Compression failed");
    }
  };

  // Funzione per eliminare il file esistente da Firebase
  const deleteExistingFile = async (fileName) => {
    const decodedFileName = decodeURIComponent(fileName); // Decodifica il percorso del file
    const fileRef = ref(storage, decodedFileName); // Utilizza il percorso decodificato
    try {
      await deleteObject(fileRef);
      console.log("File esistente eliminato.");
    } catch (error) {
      console.error("Errore durante l'eliminazione del file:", error);
    }
  };

  // Funzione per caricare i file su Firebase
  // Funzione per caricare i file su Firebase
  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      // Mappiamo i file per caricarli solo se sono validi
      const promises = files.map(async (file) => {
        console.log("Caricamento file:", file); // Log del file che sta per essere caricato

        let processedFile = file;

        // Se il file è un'immagine, comprimilo prima di caricarlo
        if (file.type.startsWith("image/")) {
          processedFile = await compressImage(file);
        }

        // Verifica se il nome del file è valido
        if (!processedFile.name) {
          console.warn(
            "Nome del file non valido, ignorando il file:",
            processedFile
          );
          return null; // Non caricare se il nome è undefined
        }

        const fileRef = ref(
          storage,
          `${folder}/${title}/${processedFile.name}`
        );

        // Caricamento su Firebase Storage
        await uploadBytes(fileRef, processedFile);
        const url = await getDownloadURL(fileRef);
        return url;
      });

      // Filtra i file nulli (che non hanno URL valido)
      const uploadedUrls = (await Promise.all(promises)).filter(
        (url) => url !== null && url !== undefined // Assicurati che l'URL sia valido
      );

      if (uploadedUrls.length > 0) {
        // Aggiungi i nuovi URL alla lista esistente di URL
        setUrls((prevUrls) => [...prevUrls, ...uploadedUrls]);
        onChange([...urls, ...uploadedUrls]); // Notifica il componente genitore con la nuova lista di URL

        // Aggiungi anche le nuove immagini alla lista delle immagini visibili
        setVisibleImages((prevImages) => [...prevImages, ...uploadedUrls]);
      } else {
        console.warn("Nessun file valido caricato.");
      }
    } catch (error) {
      setError(error);
    } finally {
      setUploading(false);
    }
  };

  // Funzione per gestire il cambiamento del file
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (multiple) {
      setImagePreviews(files.map((file) => URL.createObjectURL(file)));
      uploadFiles(files);
    } else {
      setImagePreviews([URL.createObjectURL(files[0])]);
      uploadFiles(files);
    }
  };

  // Funzione per rimuovere un'immagine
  const removeImage = (imageUrl) => {
    const fileName = imageUrl.split("?")[0].split("/").pop(); // Ottieni il nome del file senza la query string
    console.log("File name to delete:", fileName); // Aggiungi un log per il debug

    // Rimuovi il file da Firebase Storage
    deleteExistingFile(fileName);

    // Rimuovi l'immagine dalla lista degli URL e dalla lista delle anteprime
    const updatedUrls = urls.filter((url) => url !== imageUrl);
    const updatedPreviews = imagePreviews.filter(
      (preview) => preview !== imageUrl
    );
    setUrls(updatedUrls);
    setImagePreviews(updatedPreviews);
    onChange(updatedUrls); // Notifica il componente genitore

    // Rimuovi l'immagine da `visibleImages` per aggiornare l'interfaccia
    const updatedVisibleImages = visibleImages.filter(
      (image) => image !== imageUrl
    );
    setVisibleImages(updatedVisibleImages); // aggiorna le immagini visibili
  };

  // Aggiorna la lista delle immagini visibili con la lista `planimetria?.photos`
  useEffect(() => {
    setVisibleImages(images);
  }, [images]);

  return (
    <Box>
      <Typography variant="body1">{label}</Typography>
      <input
        type="file"
        name={field}
        multiple={multiple}
        accept={accept}
        onChange={handleFileChange}
      />
      {isEdit && (
        <Stack width={"100%"} flexDirection={"row"} flexWrap="wrap" gap={2}>
          {visibleImages
            .filter((image) => image !== undefined)
            .map((image, index) => (
              <Box key={index} position="relative">
                <img
                  src={image} // Qui supponiamo che `image` sia l'URL dell'immagine
                  alt={`property-image-${index}`}
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    cursor: "pointer", // Per far capire che l'immagine è cliccabile
                  }}
                  onClick={() => removeImage(image)} // Rimuovi l'immagine al click
                />
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  style={{
                    position: "absolute",
                    top: "0",
                    right: "0",
                    padding: "5px",
                    cursor: "pointer",
                  }}
                  onClick={() => removeImage(image)} // Rimuovi immagine
                >
                  X
                </Button>
              </Box>
            ))}
        </Stack>
      )}

      {/* {imagePreviews.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", marginTop: "10px" }}>
          {imagePreviews.map((preview, index) => (
            <img
              key={index}
              src={preview}
              alt={`preview-${index}`}
              style={{
                width: "100px",
                height: "100px",
                marginRight: "10px",
                borderRadius: "8px",
                objectFit: "cover",
                cursor: "pointer", // Aggiungi il cursore per far capire che è cliccabile
              }}
              onClick={() => removeImage(preview)} // Rimuovi l'immagine al click
            />
          ))}
        </div>
      )} */}

      {uploading && (
        <Typography variant="body2">Caricamento in corso...</Typography>
      )}
      {error && (
        <Typography variant="body2" color="error">
          {error.message}
        </Typography>
      )}
    </Box>
  );
};

export default FileUpload;
